import { useState } from "react";
import { QrCode, Upload, Trash2, ShieldCheck, ShieldOff, Eye, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BADGE = {
  ACTIVE: ["bg-emerald-50 text-emerald-700", "QR active in reminders"],
  UNVERIFIED: ["bg-amber-50 text-amber-700", "Saved — not verified (not shown to customers)"],
  NOT_SET_UP: ["bg-slate-100 text-slate-600", "QR not set up"],
  NOT_INR: ["bg-slate-100 text-slate-500", "UPI QR: INR accounts only"],
  INACTIVE: ["bg-rose-50 text-rose-600", "Account inactive — QR off"],
};

/** Approved UPI ID / bank-issued QR for ONE Bank Master account. Any change voids verification. */
export function BankQrPanel({ account, canEdit, canVerify, onChanged }) {
  const qr = account.qr || { state: "NOT_SET_UP" };
  const [upi, setUpi] = useState(account.upi_id || "");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const id = account.id;
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setPreview(null); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const upload = (file) => {
    if (!file) return;
    const rd = new FileReader();
    rd.onload = () => run(() => api.post(`/bank-accounts/${id}/qr-image`, { data_url: rd.result }), "Bank QR saved — verify to activate");
    rd.readAsDataURL(file);
  };
  const showPreview = async () => {
    try { const { data } = await api.get(`/bank-accounts/${id}/qr-preview`); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No preview"); }
  };
  const [cls, label] = BADGE[qr.state] || BADGE.NOT_SET_UP;
  const hasDest = !!(account.upi_id || account.qr_image_payload);
  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-2 text-[11px]" data-testid={`bank-qr-${id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <QrCode className="h-3.5 w-3.5 text-slate-500" />
        <span className="font-semibold text-slate-700">Payment QR</span>
        <span className={`px-1.5 py-0.5 rounded ${cls}`} data-testid={`bank-qr-state-${id}`}>{label}</span>
        {qr.vpa && <span className="font-mono text-slate-600" data-testid={`bank-qr-vpa-${id}`}>{qr.vpa}</span>}
        {qr.state === "ACTIVE" && qr.verified_by && <span className="text-slate-400">verified by {qr.verified_by}</span>}
      </div>
      {canEdit && qr.state !== "NOT_INR" && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <Input value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="Approved UPI ID (e.g. name@bank)" className="h-8 w-56 font-mono text-[12px]" data-testid={`bank-qr-upi-input-${id}`} />
          <Button size="sm" variant="outline" disabled={busy || upi.trim() === (account.upi_id || "")} data-testid={`bank-qr-upi-save-${id}`}
            onClick={() => run(() => api.put(`/bank-accounts/${id}/upi`, { upi_id: upi.trim() || null }), upi.trim() ? "UPI ID saved — verify to activate" : "UPI ID cleared")}>Save UPI ID</Button>
          <label className="inline-flex items-center gap-1 cursor-pointer rounded border border-slate-300 bg-white px-2 h-8 hover:bg-slate-100" data-testid={`bank-qr-upload-${id}`}>
            <Upload className="h-3.5 w-3.5" /> {account.qr_image_payload ? "Replace bank QR" : "Upload bank QR"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`bank-qr-file-${id}`} />
          </label>
          {account.qr_image_payload && <button className="text-rose-500 hover:bg-rose-50 rounded p-1" disabled={busy} data-testid={`bank-qr-remove-${id}`}
            onClick={() => run(() => api.delete(`/bank-accounts/${id}/qr-image`), "Bank QR removed")}><Trash2 className="h-3.5 w-3.5" /></button>}
          {hasDest && <Button size="sm" variant="ghost" onClick={showPreview} data-testid={`bank-qr-preview-btn-${id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Scan-check</Button>}
          {canVerify && hasDest && (qr.state === "ACTIVE"
            ? <Button size="sm" variant="outline" disabled={busy} data-testid={`bank-qr-unverify-${id}`} onClick={() => run(() => api.post(`/bank-accounts/${id}/qr-verify`, { verified: false }), "QR deactivated")}><ShieldOff className="h-3.5 w-3.5 mr-1" /> Deactivate</Button>
            : <Button size="sm" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`bank-qr-verify-${id}`} onClick={() => window.confirm(`Confirm ${qr.vpa} is ${account.account_holder_name || "the company"}'s approved UPI destination for this account?`) && run(() => api.post(`/bank-accounts/${id}/qr-verify`, { verified: true }), "QR verified — active in reminders")}><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify for QR</Button>)}
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        </div>)}
      {preview && (
        <div className="mt-2 flex items-start gap-3" data-testid={`bank-qr-preview-${id}`}>
          <img src={preview.data_url} alt="QR preview" className="h-28 w-28 border border-slate-200 bg-white" />
          <div className="min-w-0"><div className="text-slate-500">Encodes exactly (amount + invoice note are added per reminder for UPI IDs):</div>
            <div className="font-mono break-all text-slate-800" data-testid={`bank-qr-payload-${id}`}>{preview.payload}</div></div>
        </div>)}
    </div>
  );
}
